import React from 'react'
import styled from '@emotion/styled'
import type { BetType } from '@mobi/api-types/racePage/types'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { toTitleCase } from '@mobi/utils/string'
import * as EventAggregator from '@core/Areas/RaceCard/Components/RaceCardContent/Components/LegacyKnockout/ViewModels/AppUtils/Framework/Messaging/EventAggregator'
import { CHANGE_BET_TYPE_EVENT } from '@core/Areas/RaceCard/constants'
import type { RaceListResponse } from '@core/Areas/Racing/Hooks/useRaceList/types'
import { useRaceList } from '@core/Areas/Racing/Hooks/useRaceList/useRaceList'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { getPayIndicatorText, setSelectedRace } from '@core/Areas/Racing/helpers'
import { getRaceLink } from '@core/Areas/Racing/helpers/getRaceLink'
import { DateTime } from '@core/Components/DateTime'
import { appHistory } from '@core/Utils/routing/history'
import { WeatherIcon } from '../WeatherIcon'
import { MeetingsDropdown, PayoutInformation, RaceInfoHeader, RaceSelection } from './Components'
import { SkeletonLoader } from './Components/SkeletonLoader'

export const RaceNavigation: React.FC<{
  raceNumber: number
  meetingId: string
  meetingDate: string
  meetingSeq?: string
  product?: string
  fooPayIndicator?: number
  isRacingWA?: boolean | null
}> = ({ raceNumber, meetingId, meetingDate, meetingSeq, product, fooPayIndicator, isRacingWA }) => {
  const isAllUpSelected = useIsAllUpSelected()
  const { meeting } = useRaceList(meetingDate, meetingId, meetingSeq, product)

  const races = meeting?.races
  const currentRace = races?.find(race => race.raceNumber === raceNumber)

  const hasData = meeting != undefined && races != undefined && currentRace != undefined
  if (!hasData) return <SkeletonLoader />

  const isTrackConditionKnown = meeting.courseCondition.toLowerCase() !== 'unknown'

  const onRaceSelected = (races: RaceListResponse['races'], raceNumber: number): void => {
    const selectedRace = races.find(race => race.raceNumber === raceNumber)

    if (selectedRace != undefined) {
      // To determine whether we are navigating between FOO and Matched
      const bothTote = !currentRace.fixed && !selectedRace?.fixed
      if (bothTote) setSelectedRace(selectedRace.raceNumber)
      else {
        const selectRaceLink = getRaceLink({
          meetingId: meeting.meetingId,
          raceNumber: selectedRace.raceNumber,
          meetingDate: meeting.meetingDate,
          code: meeting.meetingCode,
          meetingSeq: meeting.fixed?.competitionSequence,
          product: selectedRace.fixed?.product,
          raceSeq: selectedRace.fixed?.eventSequence,
          isFOO: !!selectedRace.fixed,
        })
        appHistory.push(selectRaceLink)
      }
    }
  }

  return (
    <>
      <RaceNavigationStyled>
        <div>
          <MeetingsDropdown
            meetingName={meeting.courseName}
            meetingCode={meeting.meetingCode.toLowerCase() as RaceCodesLowerCase}
            meetingDate={meeting.meetingDate}
          />

          <div>
            <span>
              <DateTime date={new Date(currentRace.advertisedStartTime)} format='ddd MMM D' />
            </span>
            {isTrackConditionKnown && (
              <span data-testid='track-condition'>{toTitleCase(meeting.courseCondition)}</span>
            )}{' '}
            <WeatherIcon courseWeather={meeting.courseWeather} />
          </div>
        </div>

        {!isAllUpSelected && (
          <div style={{ overflow: 'hidden' }}>
            <RaceSelection
              meetingId={meeting.meetingId}
              meetingDate={meeting.meetingDate}
              races={races}
              selectedRace={currentRace.raceNumber}
              onRaceSelected={raceNumber => onRaceSelected(races, raceNumber)}
              firstLegsOfQuaddies={
                new Set(races.filter(race => race.quaddie.includes(1)).map(race => race.raceNumber))
              }
            />
          </div>
        )}
      </RaceNavigationStyled>

      {!isAllUpSelected && (
        <>
          <RaceInfoHeader
            currentRace={currentRace}
            meetingDate={meetingDate}
            meetingId={meeting.meetingId}
            courseName={meeting.courseName}
            isRacingWA={isRacingWA}
          />

          <PayoutInformation
            totePayoutInformation={getPayIndicatorText(currentRace.totePayIndicator) ?? ''}
            fixedPayoutInformation={
              getPayIndicatorText(fooPayIndicator ?? currentRace.fixedPayIndicator) ?? ''
            }
          />
        </>
      )}

      <hr />
    </>
  )
}

// =============
// Local Helpers
// =============

const useIsAllUpSelected = () => {
  const [isAllUp, setIsAllUp] = React.useState(false)

  React.useEffect(() => {
    EventAggregator.subscribe(CHANGE_BET_TYPE_EVENT, (params: { betType: BetType }) =>
      setIsAllUp(params.betType === 'AllUp')
    )
    return () => {
      EventAggregator.unsubscribe(CHANGE_BET_TYPE_EVENT, (params: { betType: BetType }) =>
        setIsAllUp(params.betType === 'AllUp')
      )
    }
  }, [])

  return isAllUp
}

// ======
// Styles
// ======

const RaceNavigationStyled = styled.nav({
  paddingTop: '0.5rem',
  color: colors.black,
  fontFamily: font.family.primary,
  fontSize: font.size.sm.fontSize,
  lineHeight: font.size.sm.lineHeight,

  // Meeting Dropdown & Weather
  '> div:first-of-type': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: spacing.md,
    paddingRight: spacing.md,

    '> div:last-of-type': {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',

      '> span:first-of-type': {
        fontWeight: font.weight.semibold,
        paddingRight: spacing.smx1,
      },
    },
  },

  // Race Number Selection
  '> div:nth-of-type(2)': {
    overflow: 'hidden',
  },

  '& ~ hr:first-of-type': {
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    border: 0,
    height: '0.05rem',
    borderBottom: '0.05rem solid ' + colors.neutral[200],
  },
})
